import { Text, TextLink, VSpacer } from '@/components/DesignSystem';
import { FilterSelector } from "@/components/DesignSystem/Toolbar/FilterSelector";
import {
  FilterSelection,
  InfiniteScrollOptions,
  NonBooleanFilter,
} from "@/components/DesignSystem/Toolbar/interfaces";
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { SXStyles } from '@/themes/variant-interfaces/SXStyles';
import Close from '@mui/icons-material/Close';
import { Box, IconButton, Menu, MenuProps, Stack } from "@mui/material";
import { Modify } from "@shared/utilities/UtilityTypes";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const styles: SXStyles = {
  clearButton: {
    paddingRight: '10px',
    paddingY: '8px',
  },
} as const;

interface FilterMenuProps
  extends Modify<
    MenuProps,
    { onChange: (selection: FilterSelection) => void }
  > {
  filter: NonBooleanFilter,
  onClose: () => void,
  selection?: FilterSelection,
  testID: string,
  themeColor?: string,
}

const FilterMenuContainer = ({
  infiniteScrollOptions,
  children,
}: { children: ReactNode, infiniteScrollOptions?: InfiniteScrollOptions }) => (
  infiniteScrollOptions ? (
    <InfiniteScroll
     fetchMoreItems={infiniteScrollOptions.fetchMoreItems}
     hasMore={infiniteScrollOptions.hasMore ?? true}
     height="80vh"
     isLoading={infiniteScrollOptions.isLoading}
    >
     {children}
    </InfiniteScroll>
  ) : (
    <>{children}</>
  )
);

export const FilterMenu = ({
  anchorEl,
  filter,
  onChange,
  onClose,
  selection = new Set(),
  testID,
  themeColor,
}: FilterMenuProps) => {
  const { t } = useTranslation();
  return (
    <Menu
      PaperProps={{
        style: {
          borderRadius: '12px',
          marginTop: '12px',
          padding: '12px',
        },
      }}
      anchorEl={anchorEl}
      aria-rowspan={selection?.size ?? 0}
      data-testid={testID}
      onClose={onClose}
      open
    >
      <FilterMenuContainer infiniteScrollOptions={filter.infiniteScrollOptions}>
        <Stack direction="row" justifyContent="space-between" p="12px 16px">
          <IconButton
            onClick={onClose}
          >
            <Close />
          </IconButton>
          {!filter.hideClearButton && (
            <TextLink
              category="label-medium"
              disabled={!selection || selection.size === 0}
              onClick={() => {
                onChange?.(new Set());
                filter.onSearch?.('');
              }}
              sx={{
                ...styles.clearButton,
                ...(themeColor && { color: themeColor }),
              }}
              testID={`${testID}-clear-button`}
            >
              {t(($) => $.common.actions.clear)}
            </TextLink>
          )}
        </Stack>
        <Box>
          {!!filter.helperText &&
            <>
              <Text category="title-small">
                {filter.helperText}
              </Text>
              <VSpacer size="5" />
            </>
          }
          <FilterSelector
            filter={filter}
            onChange={onChange}
            selection={selection}
            testID={`${testID}-selector`}
            themeColor={themeColor}
          />
        </Box>
      </FilterMenuContainer>
    </Menu>
  );
};
